import type { CompositionNorthArrowStyle } from './model';

export type NorthArrowPaint = 'foreground' | 'white';

export interface NorthArrowCutout {
  readonly points: readonly (readonly [x: number, y: number])[];
}

export interface NorthArrowPathPrimitive {
  readonly kind: 'path';
  readonly points: readonly (readonly [x: number, y: number])[];
  readonly closed: boolean;
  readonly fill?: NorthArrowPaint;
  readonly stroke?: NorthArrowPaint;
  readonly strokeWidth: number;
  readonly lineCap?: 'butt' | 'round';
}

export interface NorthArrowCirclePrimitive {
  readonly kind: 'circle';
  readonly centre: readonly [x: number, y: number];
  readonly radius: number;
  readonly fill?: NorthArrowPaint;
  readonly stroke?: NorthArrowPaint;
  readonly strokeWidth: number;
  readonly cutouts?: readonly NorthArrowCutout[];
}

export type NorthArrowPrimitive = NorthArrowPathPrimitive | NorthArrowCirclePrimitive;

export const COMPOSITION_NORTH_ARROW_STYLE_LABELS: Readonly<
  Record<CompositionNorthArrowStyle, string>
> = {
  outline: 'Outline',
  filled: 'Filled',
  'circle-filled': 'Circle - filled',
  'circle-hollow': 'Circle - hollow',
  cross: 'Cross',
  arrow: 'Arrow',
  triangle: 'Triangle',
  'circle-arrow': 'Circle - arrow',
  'circle-classic': 'Circle - classic',
};

const path = (
  points: NorthArrowPathPrimitive['points'],
  options: Omit<NorthArrowPathPrimitive, 'kind' | 'points'>,
): NorthArrowPathPrimitive => ({ kind: 'path', points, ...options });

/** Shared 100 x 100 page-space geometry for Composer, Canvas export, and vector PDF. */
export function northArrowPrimitives(
  style: CompositionNorthArrowStyle,
): readonly NorthArrowPrimitive[] {
  switch (style) {
    case 'circle-classic':
      return [
        {
          kind: 'circle',
          centre: [50, 55],
          radius: 28,
          fill: 'white',
          stroke: 'foreground',
          strokeWidth: 2,
        },
        path(
          [
            [50, 29],
            [65, 76],
            [50, 66],
            [35, 76],
          ],
          { closed: true, fill: 'foreground', strokeWidth: 0 },
        ),
      ];
    case 'filled':
      return [
        path(
          [
            [50, 24],
            [72, 82],
            [50, 70],
            [28, 82],
          ],
          { closed: true, fill: 'foreground', stroke: 'foreground', strokeWidth: 2 },
        ),
      ];
    case 'circle-filled':
      return [
        {
          kind: 'circle',
          centre: [50, 55],
          radius: 28,
          fill: 'foreground',
          strokeWidth: 0,
          cutouts: [
            {
              points: [
                [48, 27],
                [52, 27],
                [52, 52],
                [48, 52],
              ],
            },
          ],
        },
      ];
    case 'circle-hollow':
      return [
        {
          kind: 'circle',
          centre: [50, 55],
          radius: 28,
          stroke: 'foreground',
          strokeWidth: 2,
        },
        path(
          [
            [50, 27],
            [50, 52],
          ],
          { closed: false, stroke: 'foreground', strokeWidth: 3, lineCap: 'butt' },
        ),
      ];
    case 'cross':
      return [
        path(
          [
            [50, 25],
            [50, 83],
          ],
          { closed: false, stroke: 'foreground', strokeWidth: 3 },
        ),
        path(
          [
            [30, 60],
            [70, 60],
          ],
          { closed: false, stroke: 'foreground', strokeWidth: 2 },
        ),
      ];
    case 'arrow':
      return [
        path(
          [
            [50, 83],
            [50, 28],
          ],
          { closed: false, stroke: 'foreground', strokeWidth: 3 },
        ),
        path(
          [
            [38, 40],
            [50, 28],
            [62, 40],
          ],
          { closed: false, stroke: 'foreground', strokeWidth: 2 },
        ),
      ];
    case 'triangle':
      return [
        path(
          [
            [50, 25],
            [70, 82],
            [30, 82],
          ],
          { closed: true, fill: 'foreground', strokeWidth: 0 },
        ),
      ];
    case 'circle-arrow':
      return [
        {
          kind: 'circle',
          centre: [50, 55],
          radius: 28,
          fill: 'foreground',
          strokeWidth: 0,
          cutouts: [
            {
              points: [
                [50, 28],
                [62, 77],
                [50, 68],
                [38, 77],
              ],
            },
          ],
        },
      ];
    case 'outline':
    default:
      return [
        path(
          [
            [50, 24],
            [72, 82],
            [50, 70],
            [28, 82],
          ],
          { closed: true, stroke: 'foreground', strokeWidth: 5 },
        ),
      ];
  }
}
